import { supabase } from '../lib/supabase';
import { t } from '../i18n/nl.js';

async function sendAgreementEmailAndPush(token, publicUrl, clientEmail, clientName, clientId) {
  let activationHtml = '';
  const { data: clientProfile } = await supabase.from('profiles').select('account_activated_at').eq('id', clientId).maybeSingle();
  if (!clientProfile?.account_activated_at) {
    const { data: activationData, error: activationError } = await supabase.functions.invoke('send-account-activation-email', {
      body: { client_id: clientId, send_email: false },
    });
    if (!activationError && activationData?.activation_url) {
      activationHtml = `<div style="margin:24px 0;padding:16px;background:#f0fdf4;border-left:4px solid #1d9e75"><strong>${t('accountActivation.agreementTitle')}</strong><p>${t('accountActivation.agreementText')}</p><a href="${activationData.activation_url}" style="display:inline-block;background:#1d9e75;color:white;padding:10px 18px;text-decoration:none;border-radius:6px;font-weight:600">${t('accountActivation.agreementButton')}</a></div>`;
    }
  }
  const { data: settingsRows } = await supabase
    .from('settings')
    .select('key, value')
    .in('key', ['email_agreements', 'email_from']);

  const settingsMap = {};
  settingsRows?.forEach(({ key, value }) => { settingsMap[key] = value || ''; });
  const agreementFrom = settingsMap.email_agreements?.trim() || settingsMap.email_from?.trim() || '';

  const { error: emailErr } = await supabase.functions.invoke('send-email', {
    body: {
      to: clientEmail,
      from: agreementFrom || undefined,
      subject: 'Overeenkomst ondertekenen - Fit En Sterker',
      html: `
        <p>Beste ${clientName},</p>
        <p>Je coach heeft een overeenkomst naar je verstuurd om te ondertekenen.</p>
        <p>Klik op de link hieronder om de overeenkomst te bekijken en digitaal te ondertekenen:</p>
        <p><a href="${publicUrl}" style="display:inline-block;background:#1d9e75;color:white;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:600;">Overeenkomst bekijken en ondertekenen</a></p>
        <p style="margin-top:20px;font-size:13px;color:#666;">Of kopieer deze link: <a href="${publicUrl}">${publicUrl}</a></p>
        <p style="margin-top:20px;font-size:13px;color:#666;">Deze link is 7 dagen geldig.</p>
        ${activationHtml}
        <p>Met vriendelijke groet,<br>Fit En Sterker</p>
      `,
    },
  });

  if (emailErr) {
    console.error('E-mail versturen mislukt:', emailErr);
    // Niet fataal — ga door met push notificatie
  }

  try {
    // Centrale send-poort (C2): token + push_preferences-check via RPC
    // i.p.v. een kant-en-klaar meegegeven token.
    const { data: pushableToken } = await supabase.rpc('get_pushable_token', {
      p_user_id: clientId,
      p_category: 'agreements',
    });
    if (pushableToken) {
      await supabase.functions.invoke('send-push', {
        body: {
          tokens: [pushableToken],
          title: 'Overeenkomst ondertekenen',
          body: 'Je coach heeft een overeenkomst naar je verstuurd. Open de app om te ondertekenen.',
          data: {
            type: 'agreement',
            agreementToken: token,
          },
        },
      });
    }
  } catch (pushErr) {
    console.error('Push notificatie versturen mislukt:', pushErr);
    // Niet fataal
  }
}

/**
 * Genereer een agreement token en stuur e-mail + push notificatie naar client
 * @param {string} clientId - UUID van de client
 * @param {string} coachId - UUID van de coach
 * @param {string|null} suggestedMembershipTypeId - Optioneel voorgesteld membership type
 * @param {string} clientEmail - E-mailadres van de client
 * @param {string} clientName - Naam van de client
 * @param {object} customFields - Optionele aangepaste voorwaarden
 * @returns {Promise<{token: string, publicUrl: string}>}
 */
export async function sendAgreementToClient(
  clientId,
  coachId,
  suggestedMembershipTypeId,
  clientEmail,
  clientName,
  customFields = {},
  staffelId = null,
  additionalMembershipTypes = null
) {
  // Atomisch: laat oude openstaande tokens van deze client vervallen en
  // maak het nieuwe token aan (create_agreement_token, migratie
  // 20260711100000) — voorkomt dat er twee geldige overeenkomst-links
  // tegelijk actief blijven voor dezelfde client. created_by wordt
  // server-side op auth.uid() gezet, coachId hier is niet meer nodig
  // voor de insert maar blijft in de signatuur voor bestaande aanroepers.
  const { data: token, error: tokenErr } = await supabase.rpc('create_agreement_token', {
    p_client_id: clientId,
    p_membership_type_id: suggestedMembershipTypeId,
    p_membership_type_period_id: staffelId ?? null,
    p_is_custom: customFields.isCustom || false,
    p_custom_name: customFields.customName ?? null,
    p_custom_price: customFields.customPricePerMonth ?? null,
    p_custom_credits: customFields.customCredits ?? null,
    p_additional_membership_types: additionalMembershipTypes,
  });
  if (tokenErr) throw new Error('Token aanmaken mislukt: ' + tokenErr.message);

  const publicUrl = `${window.location.origin}/overeenkomst/${token}`;

  await sendAgreementEmailAndPush(token, publicUrl, clientEmail, clientName, clientId);

  return { token, publicUrl };
}

/**
 * Coach-multi-abonnement-builder: stuur de tot dan toe gemaakte keuzes
 * (draft_selection) naar de cliënt. Los van sendAgreementToClient —
 * membership_type_id/additional_membership_types blijven leeg, zodat
 * sign-agreement/finalize_agreement_signing_multi ongewijzigd blijven.
 * @param {string} clientId
 * @param {string} coachId
 * @param {Array<{membership_type_id: string, membership_type_period_id: string|null, payment_frequency: string|null, selected_bonus_ids: string[]}>} draftSelection
 * @param {string} clientEmail
 * @param {string} clientName
 * @param {string[]} excludedTypeIds - abonnementstypes die de coach voor deze cliënt heeft uitgesloten (onderdeel 3)
 * @param {Array<{id: string, name: string, amount: number|null, credit_amount: number|null, credit_pool: string|null}>} customLineItems - losse coach-regels (bedrag en/of credit-toekenning)
 * @returns {Promise<{token: string, publicUrl: string}>}
 */
export async function sendDraftAgreementToClient(
  clientId,
  coachId,
  draftSelection,
  clientEmail,
  clientName,
  excludedTypeIds = [],
  customLineItems = []
) {
  const result = await createDraftAgreementToken(
    clientId,
    draftSelection,
    excludedTypeIds,
    customLineItems
  );

  await sendAgreementEmailAndPush(result.token, result.publicUrl, clientEmail, clientName, clientId);

  return result;
}

/**
 * Maakt uitsluitend een overeenkomsttoken voor ondertekening op locatie.
 * Er wordt bewust geen e-mail of push verstuurd.
 */
export async function createDraftAgreementToken(
  clientId,
  draftSelection,
  excludedTypeIds = [],
  customLineItems = []
) {
  // Atomisch: laat oude openstaande tokens van deze client vervallen en
  // maak het nieuwe token aan (create_agreement_token, migratie
  // 20260711100000) — zelfde route als sendAgreementToClient, geen
  // losse kopie van de invalidatielogica.
  const { data: token, error: tokenErr } = await supabase.rpc('create_agreement_token', {
    p_client_id: clientId,
    p_draft_selection: draftSelection && draftSelection.length > 0 ? draftSelection : null,
    p_excluded_membership_type_ids: excludedTypeIds && excludedTypeIds.length > 0 ? excludedTypeIds : null,
    p_custom_line_items: customLineItems && customLineItems.length > 0 ? customLineItems : null,
  });
  if (tokenErr) throw new Error('Token aanmaken mislukt: ' + tokenErr.message);

  const publicUrl = `${window.location.origin}/overeenkomst/${token}`;

  return { token, publicUrl };
}
